import { NavLink, Outlet } from "react-router-dom";
import { signOut, useSession } from "@otto/plattform";
import { TourBar } from "../../features/onboarding/components/TourBar.js";
import { useTour } from "../context/TourContext.js";
import { COURSES } from "../lib/coursePath.js";
import styles from "./shell.module.css";

/**
 * Rahmen mit den zwei Bereichen.
 *
 * Vorher standen hier vier: Grundeinstellungen, Lektionen, Releases, Schulen.
 * Die ersten drei sind aber keine Bereiche, sondern drei Schnitte durch EINEN
 * Entwurf – dass sie zusammengehören, musste die Anleitung mit einem Diagramm
 * erklären. Jetzt heißt der Ort „Lehrgang“, und die Seiten darin liegen sichtbar
 * darin (CourseLayout).
 *
 * „Schulen“ ist der zweite Bereich und hat mit dem Lehrgang nichts zu tun.
 * Deshalb steht der Speicherstand nicht mehr im Kopf, sondern in der
 * Fassungsleiste: dort bezieht er sich unmissverständlich auf den Entwurf, und
 * niemand liest ein „gespeichert“ neben einer Schulliste als Aussage über die
 * Schule. Der Pfadvergleich, der das vorher regelte, entfällt damit.
 */
export function Shell() {
  const { client, state } = useSession();
  const email = state.kind === "signedIn" ? (state.identity.user.email ?? "") : "";
  const tour = useTour();

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `${styles.navLink} ${isActive ? styles.navActive : ""}`;

  return (
    <div className={styles.frame}>
      <header className={styles.header}>
        <span className={styles.brand}>
          Admin<small>Otto tippt los</small>
        </span>

        <nav className={styles.nav}>
          <NavLink to={COURSES} className={linkClass}>
            Lehrgänge
          </NavLink>
          <NavLink to="/schools" className={linkClass}>
            Schulen
          </NavLink>
        </nav>

        <div className={styles.who}>
          {/* Der leise Verweis, der die weggelegte Leiste zurückholt. Er steht
              rechts bei den stillen Dingen und nicht in der Navigation: die
              Führung ist kein Bereich der Anwendung. Ohne ihn wäre „Später“
              endgültig, und genau das soll es nicht sein. */}
          {tour !== null && !tour.running && (
            <button type="button" className={styles.buttonQuiet} onClick={tour.reopen}>
              Erste Schritte
            </button>
          )}
          <span className={styles.email}>{email}</span>
          <button
            type="button"
            className={styles.buttonQuiet}
            onClick={() => void signOut(client)}
          >
            Abmelden
          </button>
        </div>
      </header>

      {/* Die Breite wählt die SEITE, an ihrer Route in App.tsx. Vorher hing sie
          am Pfadpräfix – und deshalb erbte die Lehrgangs*liste* die Breite der
          Arbeitsfläche und zog zwei Zeilen über 1400px auseinander.

          Die laufende Marke der Führung steht am Rahmen, das Ziel am echten
          Bedienelement. Zusammen greift die Regel in `tour.module.css` – ohne
          eine einzige gemessene Koordinate. `undefined` und nicht `null`: React
          lässt das Attribut dann ganz weg, statt es leer zu setzen. */}
      <main className={styles.content} data-tour-aktiv={tour?.mark ?? undefined}>
        {/* Unter dem Kopf, über dem Inhalt – und im Rahmen, damit ein
            Seitenwechsel sie nicht mitnimmt. */}
        <TourBar />
        <Outlet />
      </main>
    </div>
  );
}
